import { useRef, useState } from 'react';

import type { CropRect } from '@/lib/schema.types';

import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  src: string;
  value?: CropRect;
  /** `undefined` clears the crop (show full image). */
  onApply: (crop: CropRect | undefined) => void;
}

const FULL: CropRect = { x: 0, y: 0, w: 1, h: 1 };

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

export function CropModal({ open, onClose, src, value, onApply }: Props) {
  const [crop, setCrop] = useState<CropRect>(value ?? FULL);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ mode: 'move' | 'resize'; startX: number; startY: number; start: CropRect } | null>(
    null,
  );

  const onPointerDown = (mode: 'move' | 'resize') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { mode, startX: e.clientX, startY: e.clientY, start: crop };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    const dx = (e.clientX - drag.current.startX) / rect.width;
    const dy = (e.clientY - drag.current.startY) / rect.height;
    const s = drag.current.start;
    if (drag.current.mode === 'move') {
      setCrop({
        ...s,
        x: clamp01(Math.min(s.x + dx, 1 - s.w)),
        y: clamp01(Math.min(s.y + dy, 1 - s.h)),
      });
    } else {
      setCrop({
        ...s,
        w: Math.min(clamp01(s.w + dx), 1 - s.x) || 0.05,
        h: Math.min(clamp01(s.h + dy), 1 - s.y) || 0.05,
      });
    }
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const isFull = crop.x === 0 && crop.y === 0 && crop.w === 1 && crop.h === 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Crop image"
      description="Drag the box to move, drag the bottom-right handle to resize."
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onApply(undefined)}>
            Remove crop
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onApply(isFull ? undefined : crop)}>
            Apply
          </Button>
        </>
      }
    >
      <div
        ref={boxRef}
        className="relative mx-auto max-h-[50vh] w-full select-none overflow-hidden rounded-md bg-black/5"
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <img src={src} alt="" className="block w-full opacity-60" draggable={false} />
        <div
          onPointerDown={onPointerDown('move')}
          className="absolute cursor-move border-2 border-accent"
          style={{
            left: `${crop.x * 100}%`,
            top: `${crop.y * 100}%`,
            width: `${crop.w * 100}%`,
            height: `${crop.h * 100}%`,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
          }}
        >
          <div
            onPointerDown={onPointerDown('resize')}
            className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-se-resize rounded-sm border border-white bg-accent"
          />
        </div>
      </div>
    </Modal>
  );
}
